import _ from 'lodash';
import routerHelper from '$$routerHelper';
import Mousetrap from '../../../../libs/mousetrap';
import rights from '../../../../components/rights';
import InlineEditors from '../../controllers/inlineEditors';
import Tasks from '../../controllers/tasks';

const FOCUS_CLASS = 'gantt_focused';
const ROW_FOCUSED = 'row_focused';

class KeyboardNavigation {
  #rowIndex = 0;

  #startIndexColumn = 0;

  #columnIndex = 0;

  #instance = null;

  #disabled = false;

  #blockEnter = false;

  #toTaskDetail = false;

  creatingTask = false;

  needMoveDownAfterSave = false;

  init(instance) {
    this.#instance = instance;
    this.#disabled = false;
    this.creatingTask = false;
    this.#initMousetrapHandlers();
  }

  onGanttRender() {
    if (!(routerHelper.isGanttRoute() || routerHelper.isMultiProjectsRoute()) || this.#disabled) {
      return;
    }

    const columns = this.#instance.getGridColumns();

    _.each(columns, (column, i) => {
      if (column.name === 'text') {
        this.#startIndexColumn = i;
      }
    });

    if (columns[this.#columnIndex] && columns[this.#columnIndex].name === 'buttons') {
      this.#columnIndex--;
    }

    if (this.#columnIndex < this.#startIndexColumn) {
      this.#columnIndex = this.#startIndexColumn;
    }
  }

  onTaskIdChange(id, newId) {
    const task = this.#instance.getTask(newId);

    if (this.#toTaskDetail && task) {
      _.delay(() => {
        this.#instance.callEvent('onOpenTaskSettings', [task.id]);
      });

      this.#toTaskDetail = false;
    }

    _.delay(() => {
      this.creatingTask = false;
    });
  }

  setCurrentEditingRowAndColumnIndices(taskId, columnName) {
    const editedRowIndex = this.#instance.getGlobalTaskIndex(taskId);
    const editedColumnIndex = this.#instance.getGridColumns().findIndex(column => column.name === columnName);

    this.#rowIndex = editedRowIndex;
    this.#columnIndex = editedColumnIndex;
  }

  #moveTo(rowIndex, columnIndex) {
    const nextTask = this.#instance.getTaskByIndex(rowIndex);

    if (!nextTask) {
      console.warn('KeyboardNavigation: moveTo -> Next task doest not exist');

      return;
    }

    if (InlineEditors.isActive()) {
      InlineEditors.hide();
    }

    if (nextTask.type === this.#instance.config.types.button) {
      this.removeSelection();
      InlineEditors.startEdit(nextTask.id, 'text');
      this.#rowIndex = rowIndex;

      return;
    }

    const visibleColumns = this.#instance.getGridColumns();

    if (!visibleColumns[columnIndex]) {
      columnIndex = 1;
    }

    this.#columnIndex = columnIndex;
    this.#rowIndex = rowIndex;

    const node = this.getNode(nextTask.id, this.#columnIndex);

    if (!node) {
      console.warn('KeyboardNavigation: moveTo -> Node doest not exist');

      return;
    }

    this.selectNode(node);
    this.#adjustScrollPosition(nextTask);
  }

  #adjustScrollPosition(task) {
    const pos = this.#instance.getTaskPosition(task);
    const height = this.#instance.config.row_height;
    const scroll = this.#instance.getScrollState();
    let viewHeight;

    if (this.#instance.$grid_data || this.#instance.$task_data) {
      viewHeight = (this.#instance.$grid_data || this.#instance.$task_data).offsetHeight;
    } else {
      viewHeight = scroll.inner_height;
    }

    if (pos.top < scroll.y || pos.top + height > (scroll.y + viewHeight)) {
      this.#instance.scrollTo(null, pos.top - height * 5);
    }
  }

  moveToNextRow() {
    const task = this.#instance.getTaskByIndex(this.#rowIndex);
    const nextTaskId = this.#instance.getNext(task && task.id);

    if (!nextTaskId) {
      return;
    }

    const nextTask = this.#instance.getTask(nextTaskId);

    if (nextTask) {
      this.#moveTo(this.#rowIndex + 1, this.#columnIndex);
    }
  }

  moveToPreviousRow() {
    const task = this.#instance.getTaskByIndex(this.#rowIndex);
    const prevTaskId = this.#instance.getPrev(task && task.id);

    if (prevTaskId) {
      this.#moveTo(this.#rowIndex - 1, this.#columnIndex);
    }
  }

  moveToNextCell() {
    const columns = this.#instance.getGridColumns();
    const isTaskButtonType = this.#instance.getTaskByIndex(this.#rowIndex).type === this.#instance.config.types.button;

    if (this.#columnIndex < columns.length - 2 && !isTaskButtonType) { // 2 - because ignore last column
      this.#moveTo(this.#rowIndex, this.#columnIndex + 1);
    }
  }

  moveToPreviousCell() {
    const isAddTaskButton = this.#instance.getTaskByIndex(this.#rowIndex).type === this.#instance.config.types.button;

    if (this.#columnIndex > this.#startIndexColumn && !isAddTaskButton) {
      this.#moveTo(this.#rowIndex, this.#columnIndex - 1);
    }
  }

  removeSelection() {
    _.each(document.getElementsByClassName(FOCUS_CLASS), el => {
      el && el.classList.remove(FOCUS_CLASS);
    });

    _.each(document.getElementsByClassName('gantt_row'), row => {
      row && row.classList.remove(ROW_FOCUSED);
    });
  }

  selectNode(node) {
    this.removeSelection();

    if (node && node.closest('.gantt_row')) {
      node.closest('.gantt_row').classList.add(ROW_FOCUSED);
    }

    node && node.classList.add(FOCUS_CLASS);
  }

  getNode(taskId, columnIndex) {
    if (!this.#instance.isTaskExists(taskId) || !this.#instance.isTaskVisible(taskId)) return;
    if (!this.#instance.config.show_grid) {
      return this.#instance.getTaskNode(taskId);
    }
    const row = this.#instance.$grid.querySelector(`.gantt_row[${this.#instance.config.task_attribute}='${taskId}']`);

    if (row) {
      return row.childNodes[columnIndex];
    }
  }

  onGanttDestroy() {
    Mousetrap.reset();
  }

  onEditEnd(id, columnName) {
    const task = this.#instance.getTask(id);

    if (task.type === this.#instance.config.types.button && this.creatingTask && !this.#toTaskDetail && this.needMoveDownAfterSave) {
      this.moveToNextRow();
    }

    this.needMoveDownAfterSave = false;
  }

  toggleState(enable = true) {
    this.#disabled = !enable;
    Mousetrap[enable ? 'unpause' : 'pause']();
  }

  #initMousetrapHandlers() {
    Mousetrap.prototype.stopCallback = (e, element, combo) => { // called before will triggered any callback
      if (this.#disabled) {
        return true;
      }

      if (document.getElementsByClassName('webix_modal_box')[0]) {
        return true;
      }

      const windowsList = [
        'kanbanWindow',
        'manageTeamLayout',
        'attachmentsListPopup',
        'commentsListPopup',
        'taskSettingsPopup',
        'resourcesLayout',
        'calendarExceptionsPopup',
        'resourceCalendarExceptionsPopup',
        'templateEditorExceptionsPopup',
        'integrationsPopup',
        'createProjectLayout',
        'welcomeLayout',
        'paymentPopup',
        'jiraIntegrationPopup',
        'paymentPlanInfoPopup',
        // ID_VIEW_POPUP_CUSTOMGRID,
        'timeLogListLayout',
        'userLogsLayout',
      ];

      const popupsWithAnimationList = [
        'projectsPopupId',
        'notificationsTimeLineModal',
        'saveAsUserTemplate',
        'publicLinkWindow',
        'reportsFeedbackPopup',
        // 'userProfilePopup'
      ];

      if (
        windowsList.some(id => $$(id) && $$(id).isVisible())
        || popupsWithAnimationList.some(id => $$(id) && $$(id).isShown())
      ) {
        if (e.keyCode === 9) { // tab key
          e.preventDefault();
        }

        return true;
      }

      if ((` ${element.className} `).indexOf('mousetrap') > -1) {
        return false;
      }

      return element.tagName == 'INPUT' || element.tagName == 'SELECT' || element.tagName == 'TEXTAREA' || (element.contentEditable && element.contentEditable == 'true');
    };

    Mousetrap.bind({
      up: () => {
        this.moveToPreviousRow();
      },
      down: () => {
        this.moveToNextRow();
      },
      left: () => {
        this.moveToPreviousCell();
      },
      right: () => {
        this.moveToNextCell();
      },
      'shift+left': () => {
        const task = this.#instance.getTaskByIndex(this.#rowIndex);

        if (!rights.project.hasRight(task.gantt_id, 'create_task')) {
          return;
        }

        Tasks.moveOutdent(task.id);
      },
      'shift+right': () => {
        const task = this.#instance.getTaskByIndex(this.#rowIndex);

        if (!rights.project.hasRight(task.gantt_id, 'create_task')) {
          return;
        }

        Tasks.moveIndent(task.id);
      },
      tab: e => {
        if (e.preventDefault) {
          e.preventDefault();
        } else {
          e.returnValue = false;
        }

        const columns = this.#instance.getGridColumns();
        const task = this.#instance.getTaskByIndex(this.#rowIndex);

        if (task.type === this.#instance.config.types.button || this.#columnIndex >= columns.length - 2) {
          this.#columnIndex = this.#startIndexColumn;
          Mousetrap.trigger('down');
        } else {
          Mousetrap.trigger('right');
        }
      },
      'shift+tab': e => {
        if (e.preventDefault) {
          e.preventDefault();
        } else {
          e.returnValue = false;
        }

        const columns = this.#instance.getGridColumns();
        const task = this.#instance.getTaskByIndex(this.#rowIndex);

        if (task.type === this.#instance.config.types.button || this.#columnIndex <= this.#startIndexColumn) {
          this.#columnIndex = columns.length - 2;
          Mousetrap.trigger('up');
        } else {
          Mousetrap.trigger('left');
        }
      },
      'shift+enter': e => {
        const task = this.#instance.getTaskByIndex(this.#rowIndex);

        if (task.type !== this.#instance.config.types.button) {
          return this.#instance.callEvent('onOpenTaskSettings', [task.id]);
        }

        const taskType = InlineEditors.newTaskType;

        this.#toTaskDetail = true;
        InlineEditors.save();

        userExtAnalytics.log('gantt_add_task_grid_done', {
          way: 'shift + enter',
          type: taskType,
        });
      },
      enter: e => {
        const task = this.#instance.getTaskByIndex(this.#rowIndex);
        const currentColumn = this.#instance.getGridColumns()[this.#columnIndex];

        if (this.#blockEnter || this.creatingTask) {
          return;
        }

        if (!this.#blockEnter) {
          this.#blockEnter = true;
        }

        if (InlineEditors.isActive() && task.type === this.#instance.config.types.button) {
          const taskType = InlineEditors.newTaskType;

          InlineEditors.save();
          Mousetrap.trigger('down');
          userExtAnalytics.log('gantt_add_task_grid_done', {
            way: 'enter',
            type: taskType,
          });

          return;
        }

        if (InlineEditors.isActive()) {
          InlineEditors.save();
          Mousetrap.trigger('down');
        } else if (this.#instance.hasAccessToTask(task) && this.#instance.hasRightToEditField(task.gantt_id, currentColumn.data_type ? currentColumn.data_type : currentColumn.name)) {
          if (InlineEditors.hasEditor(currentColumn, task)) {
            this.removeSelection();
            InlineEditors.startEdit(task.id, currentColumn.name);
          } else {
            Mousetrap.trigger('down');
          }
        }
      },
      space: e => {
        const task = this.#instance.getTaskByIndex(this.#rowIndex);
        const currentColumn = this.#instance.getGridColumns()[this.#columnIndex];

        if (!InlineEditors.isActive() && this.#instance.hasAccessToTask(task) && this.#instance.hasRightToEditField(task.gantt_id, currentColumn.data_type ? currentColumn.data_type : currentColumn.name)) {
          if (InlineEditors.hasEditor(currentColumn, task)) {
            this.removeSelection();
            InlineEditors.startEdit(task.id, currentColumn.name);
          } else {
            Mousetrap.trigger('down');
          }
        }
      },
    });

    Mousetrap.bind(['shift+down', 'shift+up'], e => {
      if (InlineEditors.isActive()) {
        return;
      }

      const task = this.#instance.getTaskByIndex(this.#rowIndex);

      if (this.#instance.hasChild(task.id) && task.$level) {
        Tasks.toggleTask(task.id);
      }
    });

    Mousetrap.bind(['enter'], e => {
      this.#blockEnter = false;
    }, 'keyup');

    Mousetrap.bind(['esc', 'mod+z'], (e, combo) => {
      if (InlineEditors.isActive()) {
        InlineEditors.hide();
      } else if (combo === 'mod+z' && !routerHelper.isHistoryRoute()) {
        const projectId = this.#instance.ganttDataApi.getActiveProjectId();

        if (window.localStorage.getItem('introjsCtrlZ') && projectId && rights.project.hasRight(projectId, 'history')) {
          this.#instance.ganttDataApi.openHistoryWindow();
        }
      }
    });

    Mousetrap.bind(['del', 'command+backspace'], e => {
      const task = this.#instance.getTaskByIndex(this.#rowIndex);

      if (!InlineEditors.isActive() && !this.#instance.ganttDataApi.isHistory() && !this.#instance.ganttDataApi.isExport()) {
        if (task && task.type !== this.#instance.config.types.button && task.$level > 0) {
          Tasks.delete(task.id);
        }
      }
    });
  }
}

export default new KeyboardNavigation();
